<template>
  <div class="dashboard p-6">
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">{{ t('dashboard.title') }}</h1>
      <p class="text-gray-600 mt-2">{{ t('dashboard.welcome') }}</p>
    </div>

    <!-- Status Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="card">
        <div class="card-body">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span class="text-white text-sm font-medium">📦</span>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">{{ t('dashboard.totalProducts') }}</p>
              <p class="text-2xl font-semibold text-gray-900">{{ totalProducts }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <span class="text-white text-sm font-medium">🏪</span>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">{{ t('dashboard.warehouses') }}</p>
              <p class="text-2xl font-semibold text-gray-900">{{ totalWarehouses }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                <span class="text-white text-sm font-medium">📊</span>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">{{ t('dashboard.recentMovements') }}</p>
              <p class="text-2xl font-semibold text-gray-900">{{ totalMovements }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <span class="text-white text-sm font-medium">🔄</span>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">{{ t('dashboard.syncStatus') }}</p>
              <p class="text-sm font-semibold" :class="syncStatusClass">
                {{ syncStatusText }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <div class="card">
        <div class="card-header">
          <h2 class="text-lg font-semibold">{{ t('dashboard.quickActions') }}</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-2 gap-4">
            <router-link
              to="/products/new"
              class="btn btn-primary w-full"
            >
              {{ t('dashboard.addProduct') }}
            </router-link>
            <router-link
              to="/inventory/movement"
              class="btn btn-secondary w-full"
            >
              {{ t('dashboard.registerMovement') }}
            </router-link>
            <router-link
              to="/products"
              class="btn btn-ghost w-full"
            >
              {{ t('dashboard.viewProducts') }}
            </router-link>
            <button
              @click="refreshData"
              :disabled="loading"
              class="btn btn-ghost w-full"
            >
              {{ loading ? t('dashboard.refreshing') : t('dashboard.refreshData') }}
            </button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2 class="text-lg font-semibold">{{ t('dashboard.systemStatus') }}</h2>
        </div>
        <div class="card-body">
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">{{ t('dashboard.connection') }}</span>
              <span
                class="text-sm font-medium"
                :class="isOnline ? 'text-green-600' : 'text-red-600'"
              >
                {{ isOnline ? t('common.online') : t('common.offline') }}
              </span>
            </div>
            <div v-if="hasQueuedActions" class="flex justify-between items-center">
              <span class="text-sm text-gray-600">{{ t('dashboard.pendingSync') }}</span>
              <span class="text-sm font-medium text-yellow-600">
                {{ queuedActionsCount }} {{ t('dashboard.actions') }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">{{ t('dashboard.lastSync') }}</span>
              <span class="text-sm text-gray-500">
                {{ lastSyncTime || t('common.never') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="card">
      <div class="card-header">
        <h2 class="text-lg font-semibold">{{ t('dashboard.recentActivity') }}</h2>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <span class="text-gray-500">{{ t('common.loading') }}</span>
        </div>
        <div v-else-if="recentActivity.length === 0" class="text-center py-8">
          <div class="text-gray-400 text-4xl mb-2">📋</div>
          <p class="text-gray-500">{{ t('dashboard.noRecentActivity') }}</p>
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="activity in recentActivity"
            :key="activity.id"
            class="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
          >
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span class="text-blue-600 text-sm">📝</span>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ activity.description }}</p>
              <p class="text-xs text-gray-500">{{ activity.timestamp }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProductStore } from '../stores/useProductStore';
import { useMovementStore } from '../stores/useMovementStore';
import { useWarehouseStore } from '../stores/useWarehouseStore';
import { useOfflineStore } from '../stores/useOfflineStore';

const { t } = useI18n();

const productStore = useProductStore();
const movementStore = useMovementStore();
const warehouseStore = useWarehouseStore();
const offlineStore = useOfflineStore();

const loading = ref(false);

const totalProducts = computed(() => productStore.products.length);
const totalWarehouses = computed(() => warehouseStore.activeWarehouses.length);
const totalMovements = computed(() => movementStore.movements.length);

const isOnline = computed(() => offlineStore.isOnline);
const hasQueuedActions = computed(() => offlineStore.hasQueuedActions);
const queuedActionsCount = computed(() => offlineStore.queuedActionsCount);
const lastSyncTime = computed(() =>
  offlineStore.lastSyncTime ? offlineStore.lastSyncTime.toLocaleString() : null
);

const syncStatusText = computed(() => {
  if (!isOnline.value) return t('common.offline');
  if (hasQueuedActions.value) return t('dashboard.syncing');
  return t('dashboard.synced');
});

const syncStatusClass = computed(() => {
  if (!isOnline.value) return 'text-red-600';
  if (hasQueuedActions.value) return 'text-yellow-600';
  return 'text-green-600';
});

const movementTypeLabel = computed(() => ({
  IN: t('dashboard.stockIn'),
  OUT: t('dashboard.stockOut'),
  TRANSFER: t('dashboard.transfer'),
  ADJUSTMENT: t('dashboard.adjustment'),
}));

const recentActivity = computed(() =>
  movementStore.recentMovements.slice(0, 5).map((m) => {
    const product = productStore.productById(m.productId);
    const productLabel = product ? product.name : m.productId;
    const typeLabel = movementTypeLabel.value[m.movementType as keyof typeof movementTypeLabel.value] ?? m.movementType;
    return {
      id: m.id,
      description: `${typeLabel}: ${productLabel}`,
      timestamp: m.createdAt.toLocaleString(),
    };
  })
);

const refreshData = async () => {
  loading.value = true;
  try {
    await Promise.all([
      productStore.fetchProducts(),
      warehouseStore.fetchWarehouses(),
      movementStore.fetchMovements(),
    ]);
  } catch (error) {
    console.error('Error refreshing data:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await refreshData();
});
</script>

<style scoped>
.dashboard {
  @apply p-4 max-w-7xl mx-auto;
}
</style>
